import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiRequest } from "../../api/client";
import { getProjectRepository } from "../../data/projectRepository";
import {
  clearRepairSessionTicket,
  readRepairSessionTicket
} from "./repairSessionTicket";

export interface DemoClearCounts {
  clearable: number;
  ever_edited: number;
  dependency_retained: number;
  uncertain: number;
  cleared: number;
}

export interface DemoClearResult {
  dataset: "BASIC" | "PLANNING";
  condition_revision: number;
  document_revision: number;
  counts: DemoClearCounts;
  entity_counts: Record<string, DemoClearCounts>;
  timetable_preserved: boolean;
}

export function demoClearSummary(result: DemoClearResult): string {
  const { counts } = result;
  return [
    `预计可清理 ${counts.clearable} 条`,
    `因人工改动保留 ${counts.ever_edited} 条`,
    `因数据依赖保留 ${counts.dependency_retained} 条`,
    `因来源不确定保留 ${counts.uncertain} 条`
  ].join("；");
}

export function useDemoDataClear(
  projectId: string,
  dataset: "BASIC" | "PLANNING",
  onCleared: (result: DemoClearResult) => void
) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<DemoClearResult | null>(null);
  const basePath =
    dataset === "BASIC"
      ? `/projects/${projectId}/demo-data`
      : `/projects/${projectId}/planning/demo-data`;

  const previewMutation = useMutation({
    mutationFn: async () => {
      const project = await (await getProjectRepository()).read(projectId);
      return apiRequest<DemoClearResult>(`${basePath}/clear-preview`, {
        method: "POST",
        body: JSON.stringify({
          expected_condition_revision: project.condition_revision,
          expected_document_revision: project.document_revision
        })
      });
    },
    onSuccess: setPreview
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error("请先查看本次清理预估");
      return apiRequest<DemoClearResult>(`${basePath}/clear`, {
        method: "POST",
        body: JSON.stringify({
          expected_condition_revision: preview.condition_revision,
          expected_document_revision: preview.document_revision,
          confirm_clear: true
        })
      });
    },
    onSuccess: async (result) => {
      setPreview(null);
      if (result.counts.cleared > 0) {
        await invalidateStoredRepairSession(projectId);
      }
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(projectId)
      });
      onCleared(result);
    }
  });

  return {
    preview,
    previewMutation,
    clearMutation,
    start: () => {
      previewMutation.reset();
      clearMutation.reset();
      setPreview(null);
      previewMutation.mutate();
    },
    close: () => {
      previewMutation.reset();
      clearMutation.reset();
      setPreview(null);
    },
    confirm: () => clearMutation.mutate(),
    pending: previewMutation.isPending || clearMutation.isPending,
    visibleError:
      previewMutation.isError && preview === null
        ? previewMutation.error
        : null
  };
}

async function invalidateStoredRepairSession(projectId: string): Promise<void> {
  const ticket = readRepairSessionTicket(projectId);
  if (!ticket) return;
  clearRepairSessionTicket(projectId, ticket.sessionId);
  try {
    const repository = await getProjectRepository();
    await repository.invalidateRepairSession(
      ticket.sessionId,
      ticket.accessToken
    );
  } catch {
    // The server also rejects the stale condition revision and expires it in 7 days.
  }
}
